// Голосовой ввод (диктовка) через Web Speech API.
// Распознавание выполняется браузером — сервер и внешние API не требуются.

export type VoiceInputErrorKind =
  | 'denied'
  | 'no-speech'
  | 'network'
  | 'audio-capture'
  | 'generic';

interface VoiceRecognitionAlternative {
  transcript: string;
}

interface VoiceRecognitionResult {
  isFinal: boolean;
  length: number;
  [index: number]: VoiceRecognitionAlternative;
}

interface VoiceRecognitionResultList {
  length: number;
  [index: number]: VoiceRecognitionResult;
}

interface VoiceRecognitionEventLike extends Event {
  resultIndex: number;
  results: VoiceRecognitionResultList;
}

interface VoiceRecognitionErrorEventLike extends Event {
  error: string;
}

interface VoiceRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: VoiceRecognitionEventLike) => void) | null;
  onerror: ((event: VoiceRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}

type VoiceRecognitionCtor = new () => VoiceRecognitionLike;

export interface UseVoiceInputOptions {
  // Код языка для распознавания (например 'ru-RU'), запрашивается при старте
  language?: () => string;
  onFinal: (text: string) => void;
  onInterim?: (text: string) => void;
  onError?: (kind: VoiceInputErrorKind) => void;
}

const ERROR_MAP: Record<string, VoiceInputErrorKind> = {
  'not-allowed': 'denied',
  'service-not-allowed': 'denied',
  'no-speech': 'no-speech',
  network: 'network',
  'audio-capture': 'audio-capture',
};

export function useVoiceInput(options: UseVoiceInputOptions) {
  const isSupported = ref(false);
  const isListening = ref(false);

  let recognition: VoiceRecognitionLike | null = null;

  onMounted(() => {
    const ctor =
      (window as unknown as { SpeechRecognition?: VoiceRecognitionCtor })
        .SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: VoiceRecognitionCtor })
        .webkitSpeechRecognition;

    if (!ctor) return;

    isSupported.value = true;

    recognition = new ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: VoiceRecognitionEventLike) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result) continue;
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          const text = transcript.trim();
          if (text) options.onFinal(text);
        } else {
          interim += transcript;
        }
      }
      options.onInterim?.(interim.trim());
    };

    recognition.onerror = (event: VoiceRecognitionErrorEventLike) => {
      options.onError?.(ERROR_MAP[event.error] ?? 'generic');
    };

    recognition.onend = () => {
      isListening.value = false;
      options.onInterim?.('');
    };
  });

  const start = () => {
    if (!recognition || isListening.value) return;
    if (options.language) recognition.lang = options.language();
    try {
      recognition.start();
      isListening.value = true;
    } catch {
      // start() на уже запущенном экземпляре — состояние не меняем, сообщаем об ошибке
      options.onError?.('generic');
    }
  };

  const stop = () => {
    if (!recognition || !isListening.value) return;
    recognition.stop();
  };

  const toggle = () => {
    if (isListening.value) stop();
    else start();
  };

  onUnmounted(() => {
    if (!recognition) return;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    recognition.abort();
    recognition = null;
  });

  return { isSupported, isListening, start, stop, toggle };
}
